"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { ArrowLeft } from "lucide-react";
import type { EssenceDetail as EssenceDetailType } from "@/hooks/use-essence-detail";
import {
  EssenceHeading,
  EssenceBody,
  EssenceStats,
  CopyMarkdownButton,
  getSource,
  useTrackView,
} from "./essence-detail";
import { FavoriteButton } from "./favorite-button";
import { VersionSelector, HistoricalNotice } from "./version-selector";

/**
 * The dedicated Essence page: a comfortable reading column with a sticky
 * metadata rail. Version switching navigates via `?version=`.
 */
export function EssencePageView({ data }: { data: EssenceDetailType }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { essence, version, history, isLatest } = data;
  const source = getSource(version);
  const latest = essence.latestVersionNumber ?? version.versionNumber;

  useTrackView(essence.id);

  // Preserve the browse query (q, filters, sort) for the back link; drop our own `version`.
  const browseParams = new URLSearchParams(searchParams.toString());
  browseParams.delete("version");
  const browseQs = browseParams.toString();
  const backHref = browseQs ? `/?${browseQs}` : "/";

  const goToVersion = (n: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (n === latest) params.delete("version");
    else params.set("version", String(n));
    const qs = params.toString();
    router.push(qs ? `/essences/${essence.id}?${qs}` : `/essences/${essence.id}`, {
      scroll: false,
    });
  };

  const rail = (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <FavoriteButton essenceId={essence.id} variant="label" />
        <CopyMarkdownButton version={version} source={source} />
      </div>
      <EssenceStats essence={essence} />
      {history.length > 1 && (
        <div className="space-y-1.5">
          <p className="micro-label">Version</p>
          <VersionSelector
            history={history}
            currentVersion={version.versionNumber}
            latestVersion={latest}
            onChange={goToVersion}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-[1120px] px-4 py-5 sm:px-6 lg:py-8">
      <nav aria-label="Breadcrumb" className="mb-6">
        <Link
          href={backHref}
          className="inline-flex items-center gap-1.5 rounded-md text-[13px] font-medium text-ink-3 transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
        >
          <ArrowLeft className="size-3.5" />
          {browseQs ? "Back to results" : "Back to browse"}
        </Link>
      </nav>

      <div className="grid gap-10 lg:grid-cols-[minmax(0,72ch)_minmax(240px,300px)] lg:justify-between lg:gap-12 xl:gap-16">
        <article className="min-w-0">
          <header>
            <EssenceHeading version={version} size="page" />
          </header>

          {/* Rail content collapses into the header on small screens */}
          <div className="mt-6 lg:hidden">{rail}</div>

          {!isLatest && (
            <HistoricalNotice
              currentVersion={version.versionNumber}
              latestVersion={latest}
              onReturn={() => goToVersion(latest)}
              className="mt-6"
            />
          )}

          <EssenceBody version={version} source={source} className="mt-8" />
        </article>

        <aside className="hidden lg:block">
          <div className="sticky top-[5.5rem] rounded-xl border border-line bg-surface-1 p-5">
            {rail}
          </div>
        </aside>
      </div>
    </div>
  );
}
